import React, { useState, useEffect, useCallback } from "react";
import FlipClassService, {
  FlipClassPhaseConfig,
  FlipClassConfigUpdateItem,
} from "../../_services/FlipClassService";
import ApiService from "../../_services/ApiService";
import PhaseConfigCard from "./components/PhaseConfigCard";
import "./flip-class-config.css";

export interface Tag {
  id: number;
  name: string;
  displayLabel: string | null;
  code: string | null;
  typeId: number;
  typeName: string | null;
  typeCode: string | null;
  sortOrder: number | null;
  courseId: number | null;
}

const FlipClassConfig: React.FC = () => {
  // State
  const [configs, setConfigs] = useState<FlipClassPhaseConfig[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [configResponse, tagsResponse] = await Promise.all([
        FlipClassService.getConfig(),
        ApiService.getAllTags(),
      ]);

      // Sort configs by phase order: pre-class → in-class → post-class
      const phaseOrder: Record<string, number> = {
        "pre-class": 1,
        "in-class": 2,
        "post-class": 3,
      };
      const sortedConfigs = configResponse.sort(
        (a, b) => (phaseOrder[a.phase] || 99) - (phaseOrder[b.phase] || 99)
      );
      setConfigs(sortedConfigs);
      setTags(tagsResponse.data as Tag[]);
    } catch (err) {
      console.error("Error loading data:", err);
      setError("Failed to load configuration. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle config changes
  const handleConfigChange = useCallback(
    (
      id: number,
      updates: Partial<{ tags: number[]; autoAssign: boolean; description: string }>
    ) => {
      setConfigs((prevConfigs) =>
        prevConfigs.map((config) =>
          config.id === id ? { ...config, ...updates } : config
        )
      );
      setHasChanges(true);
    },
    []
  );

  // Save configuration
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const updatePayload: FlipClassConfigUpdateItem[] = configs.map((config) => ({
        id: config.id,
        flipClassConfig: {
          phase: config.phase,
          tags: config.tags,
          autoAssign: config.autoAssign,
          description: config.description,
        },
      }));

      const result = await FlipClassService.updateConfig(updatePayload);

      if (result.success) {
        setSuccessMessage("Configuration saved successfully!");
        setHasChanges(false);
      } else {
        setError("Failed to save configuration. Please try again.");
      }
    } catch (err) {
      console.error("Error saving configuration:", err);
      setError("Failed to save configuration. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Reset to last saved state
  const handleReset = () => {
    loadData();
    setHasChanges(false);
  };

  if (loading) {
    return (
      <div className="flip-class-config">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading configuration...</p>
        </div>
      </div>
    );
  }

  // Group tags by type for easier selection
  const tagsByType = tags.reduce((acc, tag) => {
    const typeName = tag.typeName || "Other";
    if (!acc[typeName]) {
      acc[typeName] = [];
    }
    acc[typeName].push(tag);
    return acc;
  }, {} as Record<string, Tag[]>);

  return (
    <div className="flip-class-config">
      {/* Header */}
      <header className="config-header">
        <div className="header-content">
          <h1>FlipClass Configuration</h1>
          <p className="header-subtitle">
            Configure phase tags, auto-assignment, and descriptions for the flipped classroom workflow
          </p>
        </div>
        <div className="header-actions">
          {hasChanges && (
            <button className="btn-reset" onClick={handleReset} disabled={saving}>
              Reset
            </button>
          )}
          <button
            className="btn-save"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </header>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}
      {successMessage && (
        <div className="alert alert-success">
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)}>&times;</button>
        </div>
      )}

      {/* Phase Cards */}
      <div className="phase-cards-container">
        {configs.length === 0 ? (
          <div className="no-config">
            <p>No phase configurations found.</p>
            <p className="hint">
              Phase configurations are stored in the lesson_plan_step_status_type_master table.
            </p>
          </div>
        ) : (
          configs.map((config) => (
            <PhaseConfigCard
              key={config.id}
              config={config}
              availableTags={tags}
              tagsByType={tagsByType}
              onChange={handleConfigChange}
            />
          ))
        )}
      </div>

      {/* Info Section */}
      <div className="info-section">
        <h3>How it works</h3>
        <ul>
          <li>
            <strong>Tags:</strong> Resources tagged with these tags will be shown to students
            during the corresponding phase.
          </li>
          <li>
            <strong>Auto-assign:</strong> When enabled, resources with matching tags will be
            automatically assigned to students when they enter this phase.
          </li>
          <li>
            <strong>Description:</strong> A user-friendly label for this phase displayed in the UI.
          </li>
        </ul>
      </div>
    </div>
  );
};

export default FlipClassConfig;
